import { Checkbox, Icon } from "@blueprintjs/core";
import { ContextMenu2, ContextMenu2ChildrenProps } from "@blueprintjs/popover2";
import classNames from "classnames";
import React, { MouseEventHandler, useEffect, useRef, useState } from "react";

import { CreatePlaybookDialog } from "../../../components/CreatePlaybookDialog/create-playbook-dialog";
import VideoIcon from "../../../images/VideoIcon";
import { MobileIconEmpty, MobileIconFilled } from "../../../images/custom-icons";
import { usePlaybookContext } from "../../../pages/Playbook/playbook-context";
import { useAppSelector } from "../../../redux/hooks";
import { SortQuery, useGetAllSeriesQuery, useGetAllTeamsQuery } from "../../../redux/slices";
import { useGetAccessUsersQuery } from "../../../redux/slices/fast-server-api";
import { SportPlay } from "../../../types";
import { MobileAccessDialog } from "../../mobile-access-dialog/mobile-access-dialog";
import { Tag } from "../../tag/tag";
import { PlaysWithSelection } from "../hooks";
import { getSortIcon, isMobileAccessFilled } from "../utils";
import { NoPlaysPrompt } from "./no-plays-prompt";
import { PlaysToDeleteDialog } from "./plays-to-delete-dialog/plays-to-delete-dialog";
import { TableContextMenu } from "./table-context-menu";
import styles from "./table.module.scss";

export interface TableBodyProps<TPlay> extends PlaysWithSelection {
  data: TPlay[];
  activePlayId: string | undefined;
  setActivePlayId: (id: string) => void;
  openPlayTab: () => void;
  openEditPlayDetails: (play: SportPlay) => void;
  someSelected: boolean;
  allSelected: boolean;
  handleCheckAll: () => void;
  sortOnField: (field: string) => void;
  sort: SortQuery | null;
  playbooksPage?: boolean;
}

export const Table = <TPlay extends SportPlay>({
  data,
  activePlayId,
  setActivePlayId,
  openPlayTab,
  selectedIds,
  dataWithSelected,
  handleCheckboxChange,
  openEditPlayDetails,
  someSelected,
  allSelected,
  handleCheckAll,
  sortOnField,
  sort,
  playbooksPage = false,
}: TableBodyProps<TPlay>) => {
  const { data: teams } = useGetAllTeamsQuery();
  const { data: series } = useGetAllSeriesQuery();

  const playIdRef = useRef<string | null>(null);

  const stopPropagation: MouseEventHandler<HTMLInputElement> = (event) => {
    return event.stopPropagation();
  };

  const [playsToDelete, setPlaysToDelete] = useState<TPlay[]>([]);
  const [playbookPlays, setPlaybookPlays] = useState<string[]>([]);

  const auth = useAppSelector((state) => state.auth);
  const { data: accessUsers } = useGetAccessUsersQuery({ accountId: auth.user?.userDetails.AccountId || 0 });
  const activeAccessUsers = accessUsers?.filter((user) => user.active) || [];
  const [mobileAccessIds, setMobileAccessIds] = useState<string[] | null>(null);
  const [mobileAccessDialogOpen, setMobileAccessDialogOpen] = useState(false);
  useEffect(() => {
    if (mobileAccessIds) {
      setMobileAccessDialogOpen(true);
    } else {
      setMobileAccessDialogOpen(false);
    }
  }, [mobileAccessIds]);

  const { selectedPlaybookPlayIds } = usePlaybookContext();

  if (data.length === 0) return <NoPlaysPrompt setActivePlayId={setActivePlayId} />;

  return (
    <>
      <table>
        <thead className={styles.headerRow}>
          <tr>
            <th className={styles.checkboxCell}>
              <Checkbox checked={allSelected} indeterminate={someSelected && !allSelected} onChange={handleCheckAll} />
            </th>
            {!playbooksPage && (
              <th className={styles.mobileCell}>
                <MobileIconEmpty width={10} height={20} />
              </th>
            )}
            <th onClick={() => sortOnField("season")}>
              Season{" "}
              <Icon
                icon={getSortIcon(sort, "season")}
                className={classNames(styles.sortArrow, { [styles.active || ""]: sort?.field === "season" })}
              />
            </th>
            <th onClick={() => sortOnField("team")}>
              Team
              <Icon
                icon={getSortIcon(sort, "team")}
                className={classNames(styles.sortArrow, { [styles.active || ""]: sort?.field === "team" })}
              />
            </th>
            <th onClick={() => sortOnField("series")}>
              Series
              <Icon
                icon={getSortIcon(sort, "series")}
                className={classNames(styles.sortArrow, { [styles.active || ""]: sort?.field === "series" })}
              />
            </th>
            <th>Tags</th>
            <th className={styles.playNameColumn} onClick={() => sortOnField("name")}>
              Play Name
              <Icon
                icon={getSortIcon(sort, "name")}
                className={classNames(styles.sortArrow, { [styles.active || ""]: sort?.field === "name" })}
              />
            </th>
          </tr>
        </thead>
        <tbody className={styles.tableBody}>
          {dataWithSelected?.map((play) => (
            <ContextMenu2
              disabled={playbooksPage}
              key={play.id}
              className="bp4-dark"
              onContextMenu={(event) => {
                const playId = event.currentTarget.dataset.playId;
                playIdRef.current = playId || null;
              }}
              content={
                <TableContextMenu
                  playIdRef={playIdRef}
                  plays={data}
                  teams={teams}
                  series={series}
                  setActivePlayId={setActivePlayId}
                  openEditPlayDetails={openEditPlayDetails}
                  selectedIds={selectedIds}
                  setPlaysToDelete={setPlaysToDelete}
                  setPlaybookPlays={setPlaybookPlays}
                  setMobileAccessIds={setMobileAccessIds}
                />
              }
            >
              {(props: ContextMenu2ChildrenProps) => (
                <tr
                  key={play.id}
                  className={classNames(props.className, {
                    [styles.active || ""]: play.id === activePlayId,
                    [styles.disabled || ""]: selectedPlaybookPlayIds.includes(play.id),
                  })}
                  onContextMenu={props.onContextMenu}
                  ref={props.ref}
                  data-play-id={play.id}
                  onClick={() => {
                    if (playbooksPage && !selectedPlaybookPlayIds.includes(play.id)) {
                      handleCheckboxChange(play?.id || "");
                    } else {
                      setActivePlayId(play.id || "");
                    }
                  }}
                  onDoubleClick={() => {
                    setActivePlayId(play.id || "");
                    openPlayTab();
                  }}
                >
                  {props.popover}
                  <td className={styles.checkboxCell}>
                    <span onClick={stopPropagation}>
                      <Checkbox
                        checked={play.selected}
                        onChange={() => {
                          !selectedPlaybookPlayIds.includes(play.id) && handleCheckboxChange(play?.id || "");
                        }}
                      />
                    </span>
                  </td>
                  {!playbooksPage && (
                    <td className={styles.mobileCell}>
                      <MobileIconFilled
                        width={10}
                        height={20}
                        fill={
                          isMobileAccessFilled({
                            activeUsers: activeAccessUsers,
                            playRecipientIds: play.recipients.map((rec) => rec.recipientId),
                          })
                            ? "#000"
                            : "#CCCCCC"
                        }
                        onClick={() => setMobileAccessIds([play.id])}
                      />
                    </td>
                  )}
                  <td title={play.season}>{play.season}</td>
                  <td title={play.team?.name}>{play?.team?.name}</td>
                  <td title={play.series?.name}>{play?.series?.name}</td>
                  <td title={play?.tags?.map((tag) => tag.name).join(", ")}>
                    {play?.tags?.map((tag, i) => (
                      <Tag key={i} tag={tag} className={styles.tag} />
                    ))}
                  </td>
                  <td title={play.name}>
                    <span style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>{play?.name}</span>
                      <span>
                        {play?.assets.some((asset) => asset.generated === false) ? (
                          <VideoIcon color={play.id === activePlayId ? "#FFF" : "#9a9a9a"} />
                        ) : (
                          <></>
                        )}
                      </span>
                    </span>
                  </td>
                </tr>
              )}
            </ContextMenu2>
          ))}
        </tbody>
      </table>
      <PlaysToDeleteDialog
        playsToDelete={playsToDelete}
        onClose={() => setPlaysToDelete([])}
        isOpen={playsToDelete.length > 0}
      />
      <CreatePlaybookDialog
        isOpen={playbookPlays.length > 0}
        onClose={() => setPlaybookPlays([])}
        playbookPlays={playbookPlays}
      />
      <MobileAccessDialog
        open={mobileAccessDialogOpen}
        onClose={() => setMobileAccessIds(null)}
        accessUsers={activeAccessUsers}
        plays={data.filter((play) => mobileAccessIds?.includes(play.id) || false)}
        playIds={mobileAccessIds || []}
      />
    </>
  );
};
